import { Check, X, ShieldCheck } from "lucide-react";
import { ACCENT_CLASSES } from "./tiers";

function FeatRow({ ok, text }) {
  return (
    <li className="flex items-start gap-2">
      {ok
        ? <Check size={14} className="text-emerald-400 flex-shrink-0 mt-0.5"/>
        : <X size={14} className="text-slate-600 flex-shrink-0 mt-0.5"/>}
      <span className={ok ? "text-slate-200" : "text-slate-500 line-through"}>{text}</span>
    </li>
  );
}

export default function PlanCard({ plan, isCurrent, loadingPkg, portalLoading, betaMode, adminGranted, t, onStartCheckout, onOpenPortal, onRequestCancel }) {
  const accent = ACCENT_CLASSES[plan.accent];
  const loading = loadingPkg === plan.package_id;
  const Icon = plan.Icon;

  return (
    <div
      className={`tech-card p-6 relative overflow-hidden flex flex-col ${isCurrent ? accent.border + " ring-1" : "border-slate-800"} ${plan.popular ? "lg:scale-105 lg:-translate-y-1" : ""}`}
      data-testid={`plan-${plan.id}`}
    >
      {plan.popular && (
        <div className="absolute top-0 right-0 px-3 py-1 rounded-bl-md bg-gradient-to-br from-amber-500 to-amber-600 text-white text-[10px] font-bold tracking-wider">
          {t("billing.popular")}
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className={`label-tech flex items-center gap-1 ${accent.text}`}><Icon size={14} /> Plan</div>
          <div className="font-display font-bold text-2xl text-slate-100">{plan.name}</div>
        </div>
        {isCurrent && (
          <span className={`text-[10px] label-tech px-2 py-1 rounded ${accent.bg} ${accent.text} border ${accent.border.split(" ")[0]}`}>
            {t("billing.current")}
          </span>
        )}
      </div>

      <div className="font-mono-data text-4xl text-slate-100">
        €{plan.price}<span className="text-sm text-slate-500">{t("landing.pricing.mo")}</span>
      </div>
      <div className="text-xs text-slate-500 mt-1 mb-4 min-h-[32px]">{plan.tagline}</div>

      <ul className="space-y-2 text-sm flex-grow">
        {plan.features.map((f, i) => (
          <FeatRow key={`${plan.id}-feat-${i}`} ok={f.ok} text={f.text} />
        ))}
      </ul>

      <div className="mt-6">
        {isCurrent && plan.id !== "free" && adminGranted ? (
          <div className="space-y-2">
            <div className="px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs flex items-center gap-2">
              <ShieldCheck size={14}/> {t("billing.active")}
            </div>
            <div className="px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/40 text-xs" data-testid="admin-granted-note">
              <div className="text-amber-200 font-semibold">{t("billing.adminGranted")}</div>
              <div className="text-slate-400 mt-0.5">{t("billing.adminGrantedBody")}</div>
            </div>
          </div>
        ) : isCurrent && plan.id !== "free" ? (
          <div className="space-y-2">
            <div className="px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs flex items-center gap-2">
              <ShieldCheck size={14}/> {t("billing.active")}
            </div>
            <button
              onClick={onOpenPortal}
              disabled={portalLoading}
              className="w-full px-3 py-2 rounded-md text-xs bg-slate-800 text-slate-100 border border-slate-700 hover:border-amber-500 hover:text-amber-300 disabled:opacity-50"
              data-testid="manage-subscription-btn"
            >
              {portalLoading ? t("common.loading") : t("billing.manage")}
            </button>
            <button
              onClick={onRequestCancel}
              className="w-full px-3 py-2 rounded-md text-xs bg-transparent text-red-300 border border-red-500/40 hover:bg-red-500/10"
              data-testid="cancel-subscription-btn"
            >
              {t("billing.cancelPlan")}
            </button>
          </div>
        ) : isCurrent && plan.id === "free" ? (
          <div className="px-3 py-2 rounded-md bg-slate-800/60 border border-slate-700 text-slate-400 text-xs text-center">
            {t("billing.currentLabel")}
          </div>
        ) : plan.id === "free" ? (
          <div className="px-3 py-2 rounded-md bg-slate-800/60 border border-slate-700 text-slate-500 text-xs text-center">
            {t("billing.freePlan")}
          </div>
        ) : betaMode ? (
          <div className="px-3 py-2.5 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs text-center font-medium" data-testid={`upgrade-${plan.id}-disabled`}>
            {t("billing.unlockedInBeta", { defaultValue: "Sbloccato in beta" })}
          </div>
        ) : (
          <button
            onClick={() => onStartCheckout(plan.package_id)}
            disabled={!!loadingPkg}
            className={`w-full px-4 py-2.5 rounded-md font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 text-white ${accent.btn}`}
            data-testid={`upgrade-${plan.id}-btn`}
          >
            {loading ? t("common.loading") : `${t("billing.upgrade")} ${plan.name}`}
          </button>
        )}
      </div>
    </div>
  );
}
